/**
 * Схемы ответов API.
 *
 * Ответы проверяются во время выполнения, а не только типизируются:
 * несовпадение контракта должно падать явной ошибкой, а не превращаться
 * в `undefined`, всплывающий где-то в разметке через три экрана.
 */
import { z } from 'zod';

export const profileSchema = z.object({
  telegram_id: z.number(),
  username: z.string().nullable(),
  first_name: z.string().nullable(),
  language: z.string(),
  referral_code: z.string().nullable(),
  registered_at: z.string(),
  consent_accepted: z.boolean(),
});

export const accountStateSchema = z.enum([
  'no_subscription',
  'trial',
  'active',
  'expiring_soon',
  'grace',
  'expired',
  'traffic_exhausted',
  'banned',
]);

export const subscriptionItemSchema = z.object({
  id: z.string(),
  state: accountStateSchema,
  plan_code: z.string().nullable(),
  plan_title: z.string().nullable(),
  period_code: z.string().nullable(),
  expires_at: z.string().nullable(),
  days_left: z.number().nullable(),
  is_lifetime: z.boolean(),
  traffic_limit_bytes: z.number().nullable(),
  traffic_used_bytes: z.number(),
  device_limit: z.number().nullable(),
  has_access: z.boolean(),
  subscription_url: z.string().nullable().optional(),
  devices_used: z.number().nullable().optional(),
  // Сервер «Белый список» (CDN): объём на подписку; null — без ограничения.
  cdn_limit_bytes: z.number().nullable().optional(),
  cdn_used_bytes: z.number().nullable().optional(),
  cdn_exhausted: z.boolean().optional(),
});

export const deviceSchema = z.object({
  hwid: z.string(),
  title: z.string(),
  platform: z.string().nullable(),
  os_version: z.string().nullable(),
  app: z.string().nullable(),
  added_at: z.string(),
  last_seen_at: z.string(),
});

export const devicesSchema = z.object({
  limit: z.number().nullable(),
  devices: z.array(deviceSchema),
});

export type Device = z.infer<typeof deviceSchema>;

/**
 * Сводка по аккаунту и список его подписок.
 *
 * Подписок может быть несколько: каждая даёт свои устройства. Сводка
 * считается на сервере, а не складывается здесь, — иначе бот, приложение
 * и уведомления однажды покажут разные цифры.
 */
export const subscriptionSchema = z.object({
  state: accountStateSchema,
  days_left: z.number().nullable(),
  has_access: z.boolean(),
  device_limit_total: z.number().nullable(),
  traffic_limit_bytes: z.number().nullable(),
  traffic_used_bytes: z.number(),
  items: z.array(subscriptionItemSchema),
});

export const priceSchema = z.object({
  currency: z.string(),
  amount_minor: z.number(),
  formatted: z.string(),
});

export const periodSchema = z.object({
  code: z.string(),
  duration_days: z.number().nullable(),
  prices: z.array(priceSchema),
});

export const planSchema = z.object({
  code: z.string(),
  title: z.string(),
  description: z.string().nullable(),
  traffic_limit_bytes: z.number().nullable(),
  device_limit: z.number().nullable(),
  periods: z.array(periodSchema),
});

export const plansSchema = z.array(planSchema);

// Пакеты гигабайт для сервера «Белый список».
export const cdnPackSchema = z.object({
  code: z.string(),
  gb: z.number(),
  prices: z.array(priceSchema),
});

export const cdnPacksSchema = z.object({
  plan_code: z.string().nullable(),
  packs: z.array(cdnPackSchema),
  balance_bytes: z.number(),
  gb_per_month: z.number().nullable(),
});

export const starsInvoiceSchema = z.object({
  payment_id: z.string(),
  invoice_link: z.string(),
  amount: z.number(),
});

export const lavaInvoiceSchema = z.object({
  payment_id: z.string(),
  pay_url: z.string(),
  amount_minor: z.number(),
});

export const paymentMethodsSchema = z.object({
  stars: z.boolean(),
  sbp: z.boolean(),
  card: z.boolean(),
});

export const paymentStatusSchema = z.object({
  id: z.string(),
  status: z.enum(['pending', 'succeeded', 'failed', 'cancelled', 'refunded']),
  subscription_id: z.string().nullable(),
  renewed: z.boolean(),
  expires_at: z.string().nullable(),
  gift_id: z.string().nullable().optional(),
  gift_code: z.string().nullable().optional(),
  tickets: z
    .array(z.object({ raffle_title: z.string(), first_no: z.number().nullable(), count: z.number() }))
    .optional(),
  spins: z.number().optional(),
});

export const giftItemSchema = z.object({
  id: z.string(),
  code: z.string(),
  status: z.enum(['pending', 'activated', 'cancelled', 'refunded']),
  duration_days: z.number().nullable(),
  price_minor: z.number(),
  currency: z.string(),
  message: z.string().nullable(),
  created_at: z.string(),
  activated_at: z.string().nullable(),
  recipient: z.string().nullable(),
});

export const giftsSchema = z.object({ items: z.array(giftItemSchema) });

export const giftShareSchema = z.object({ prepared_message_id: z.string() });

export const paymentHistorySchema = z.object({
  items: z.array(
    z.object({
      id: z.string(),
      status: z.enum(['succeeded', 'refunded']),
      amount_minor: z.number(),
      currency: z.string(),
      duration_days: z.number().nullable(),
      cdn_pack_gb: z.number().nullable(),
      is_gift: z.boolean(),
      paid_at: z.string(),
      refunded_at: z.string().nullable(),
    }),
  ),
});

export const referralPayoutSchema = z.object({
  id: z.string(),
  amount_minor: z.number(),
  network: z.enum(['usdt_trc20', 'usdt_ton']),
  address: z.string(),
  status: z.enum(['requested', 'completed', 'rejected']),
  created_at: z.string(),
  processed_at: z.string().nullable(),
  tx_hash: z.string().nullable(),
  paid_amount: z.string().nullable(),
  reject_reason: z.string().nullable(),
});

export const referralSchema = z.object({
  code: z.string().nullable(),
  rate_percent: z.number(),
  first_rate_percent: z.number(),
  hold_days: z.number(),
  currency: z.string(),
  min_payout_minor: z.number(),
  available_minor: z.number(),
  pending_minor: z.number(),
  earned_minor: z.number(),
  withdrawn_minor: z.number(),
  invited: z.number(),
  paying: z.number(),
  accruals: z.array(
    z.object({
      id: z.string(),
      name: z.string().nullable(),
      duration_days: z.number().nullable(),
      amount_minor: z.number(),
      created_at: z.string(),
      available_at: z.string(),
      reversed: z.boolean(),
    }),
  ),
  payouts: z.array(referralPayoutSchema),
});

export type Profile = z.infer<typeof profileSchema>;
export type Subscription = z.infer<typeof subscriptionSchema>;
export type SubscriptionItem = z.infer<typeof subscriptionItemSchema>;
export type AccountState = z.infer<typeof accountStateSchema>;
export type Plan = z.infer<typeof planSchema>;
export type CdnPack = z.infer<typeof cdnPackSchema>;
export type Period = z.infer<typeof periodSchema>;
export type Price = z.infer<typeof priceSchema>;
export type PaymentStatus = z.infer<typeof paymentStatusSchema>;
export type GiftItem = z.infer<typeof giftItemSchema>;
export type PaymentHistory = z.infer<typeof paymentHistorySchema>;
export type Referral = z.infer<typeof referralSchema>;
export type ReferralPayout = z.infer<typeof referralPayoutSchema>;

const rafflePrizeSchema = z.object({
  title: z.string(),
  quantity: z.number(),
  value_minor: z.number().nullable(),
});

export const raffleSchema = z.object({
  id: z.string(),
  title: z.string(),
  description: z.string().nullable(),
  image_url: z.string().nullable(),
  status: z.enum(['active', 'drawn']),
  starts_at: z.string(),
  ends_at: z.string().nullable(),
  draw_at: z.string().nullable(),
  prizes: z.array(rafflePrizeSchema),
  total_tickets: z.number(),
  participants: z.number(),
  my_tickets: z.number(),
});

export const rafflesSchema = z.object({ items: z.array(raffleSchema) });

export const raffleDetailSchema = raffleSchema.extend({
  my_ranges: z.array(
    z.object({
      first_no: z.number().nullable(),
      count: z.number(),
      months: z.number().nullable(),
      issued_at: z.string(),
      voided: z.boolean(),
    }),
  ),
  winners: z.array(
    z.object({
      place: z.number(),
      name: z.string(),
      ticket_no: z.number().nullable(),
      prize: z.string().nullable(),
      is_me: z.boolean(),
    }),
  ),
  manual_draw: z.boolean(),
});

export type Raffle = z.infer<typeof raffleSchema>;
export type RaffleDetail = z.infer<typeof raffleDetailSchema>;

const wheelDiscountSchema = z.object({
  percent: z.number(),
  expires_at: z.string(),
  status: z.enum(['active', 'used', 'expired', 'voided']),
});

export const wheelSchema = z.object({
  spins_left: z.number(),
  segments: z.array(z.object({ code: z.string(), title: z.string() })),
  best_discount: wheelDiscountSchema.nullable(),
  discounts: z.array(wheelDiscountSchema),
  history: z.array(z.object({ code: z.string(), title: z.string(), spun_at: z.string() })),
});

export const wheelSpinSchema = z.object({
  code: z.string(),
  title: z.string(),
  kind: z.enum(['discount', 'months', 'jackpot']),
  segment: z.number(),
  spins_left: z.number(),
  discount: wheelDiscountSchema.nullable(),
  subscription_until: z.string().nullable(),
});

export type Wheel = z.infer<typeof wheelSchema>;
export type WheelSpin = z.infer<typeof wheelSpinSchema>;
